@if (helpVisible$ | async) {
<div
    class="fixed inset-0 z-[10002] bg-black/60 backdrop-blur-sm transition-opacity duration-150"
    (click)="toggleCheatSheet()"
>
    <div 
        class="fixed inset-0 flex items-start justify-center p-4 overflow-hidden"
        (click)="$event.stopPropagation()"
    >
        <div
            data-testid="hotkeys-cheatsheet"
            class="bg-white dark:bg-gray-800 rounded-lg shadow-2xl w-full max-w-6xl max-h-[calc(100vh-3rem)] flex flex-col mt-4 mb-4 overflow-hidden">
            <!-- Header -->
            <div class="flex items-center justify-between p-6 border-b border-gray-200 dark:border-gray-700">
                <h2 class="text-2xl font-bold text-gray-800 dark:text-gray-100">{{ title }}</h2>
                <div class="flex items-center gap-3">
                    <button
                        data-testid="hotkeys-cheatsheet-customize"
                        (click)="toggleCustomizeMode()"
                        class="px-4 py-2 text-sm font-medium text-blue-600 dark:text-blue-400 bg-blue-50 dark:bg-blue-900/30 hover:bg-blue-100 dark:hover:bg-blue-900/50 rounded-lg transition-colors"
                        [title]="'hotkeys.customize-shortcuts' | translate"
                    >
                        <i class="fas fa-cog mr-2"></i>
                        {{ showCustomizeMode() ? ('hotkeys.view' | translate) : ('hotkeys.customize' | translate) }}
                    </button>
                    <button
                        data-testid="hotkeys-cheatsheet-close"
                        (click)="toggleCheatSheet()"
                        class="p-2 text-gray-400 dark:text-gray-400 hover:text-gray-600 dark:hover:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
                        [title]="'common.close' | translate"
                    >
                        <i class="fas fa-times text-xl"></i>
                    </button>
                </div>
            </div>

            <!-- Search Bar -->
            <div class="p-4 border-b border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-900/50">
                <div class="relative">
                    <i class="fas fa-search absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 dark:text-gray-500"></i>
                    <input
                        data-testid="hotkeys-cheatsheet-search"
                        type="text"
                        [ngModel]="searchInput"
                        (ngModelChange)="onSearchChange($event)"
                        [placeholder]="'hotkeys.search-shortcuts' | translate"
                        class="w-full pl-10 pr-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 placeholder-gray-400 dark:placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-blue-500 dark:focus:ring-blue-400 focus:border-transparent"
                    />
                </div>
            </div>

            <!-- Two Column Layout -->
            <div class="flex-1 flex overflow-hidden">
                <!-- Left Sidebar: Categories -->
                <div class="w-64 border-r border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-900/50 overflow-y-auto">
                    <div class="p-4">
                        <h3 class="text-sm font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide mb-3">
                            {{ 'hotkeys.category.label' | translate }}
                        </h3>
                        <nav class="space-y-1">
                            <!-- All Categories Button -->
                            <button
                                (click)="selectCategory(ALL_CATEGORIES)"
                                [ngClass]="{
                                    'bg-blue-50': selectedCategory() === ALL_CATEGORIES,
                                    'dark:bg-blue-900/30': selectedCategory() === ALL_CATEGORIES,
                                    'text-blue-700': selectedCategory() === ALL_CATEGORIES,
                                    'dark:text-blue-400': selectedCategory() === ALL_CATEGORIES,
                                    'font-semibold': selectedCategory() === ALL_CATEGORIES,
                                    'text-gray-700': selectedCategory() !== ALL_CATEGORIES,
                                    'dark:text-gray-300': selectedCategory() !== ALL_CATEGORIES
                                }"
                                class="w-full text-left px-3 py-2 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors text-sm mb-2"
                            >
                                {{ 'hotkeys.all-categories' | translate }}
                            </button>
                            @for (category of categories(); track category) {
                                <button
                                    data-testid="hotkeys-cheatsheet-category"
                                    (click)="selectCategory(category)"
                                    [ngClass]="{
                                        'bg-blue-50': selectedCategory() === category,
                                        'dark:bg-blue-900/30': selectedCategory() === category,
                                        'text-blue-700': selectedCategory() === category,
                                        'dark:text-blue-400': selectedCategory() === category,
                                        'font-semibold': selectedCategory() === category,
                                        'text-gray-700': selectedCategory() !== category,
                                        'dark:text-gray-300': selectedCategory() !== category
                                    }"
                                    class="w-full text-left px-3 py-2 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors text-sm"
                                >
                                    {{ category }}
                                </button>
                            }
                        </nav>
                    </div>
                </div>

                <!-- Middle: Shortcuts List -->
                <div class="flex-1 overflow-y-auto p-6" #contentScroll>
                    @if (pendingCloseConfirmation()) {
                        <!-- Inline confirmation to discard unsaved changes (replaces blue info banner) -->
                        <div class="bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-lg p-4 mb-4">
                            <div class="flex items-start">
                                <i class="fas fa-exclamation-triangle text-amber-600 dark:text-amber-400 mt-1 mr-3"></i>
                                <div class="flex-1">
                                    <h3 class="font-semibold text-amber-900 dark:text-amber-200 mb-1">{{ 'hotkeys.unsaved-changes-discard-title' | translate }}</h3>
                                    <p class="text-sm text-amber-800 dark:text-amber-300 mb-4">{{ 'hotkeys.unsaved-changes-discard' | translate }}</p>
                                    <div class="flex gap-3">
                                        <button
                                            (click)="cancelClose()"
                                            class="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 rounded-lg transition-colors"
                                        >
                                            {{ 'common.cancel' | translate }}
                                        </button>
                                        <button
                                            (click)="confirmDiscardAndClose()"
                                            class="px-4 py-2 text-sm font-medium text-white bg-amber-600 dark:bg-amber-500 hover:bg-amber-700 dark:hover:bg-amber-600 rounded-lg transition-colors"
                                        >
                                            {{ 'common.discard' | translate }}
                                        </button>
                                    </div>
                                </div>
                            </div>
                        </div>
                    } @else if (showCustomizeMode()) {
                        <!-- Customize Mode Info -->
                        <div class="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-4 mb-4">
                            <div class="flex items-start">
                                <i class="fas fa-info-circle text-blue-600 dark:text-blue-400 mt-1 mr-3"></i>
                                <div class="flex-1">
                                    <h3 class="font-semibold text-blue-900 dark:text-blue-300 mb-1">{{ 'hotkeys.customize-shortcuts' | translate }}</h3>
                                    <p class="text-sm text-blue-800 dark:text-blue-200">
                                        {{ 'hotkeys.customize-shortcuts-description' | translate }}
                                    </p>
                                    <p class="text-sm text-blue-800 dark:text-blue-200 mt-2">
                                        {{ 'hotkeys.shortcut-record-hint' | translate }}
                                    </p>
                                </div>
                            </div>
                        </div>
                    }

                    @if (!selectedCategory()) {
                        <div class="text-center py-12">
                            <i class="fas fa-list text-gray-300 dark:text-gray-600 text-4xl mb-4"></i>
                            <p class="text-gray-500 dark:text-gray-400 text-lg">{{ 'hotkeys.select-category' | translate }}</p>
                        </div>
                    } @else if (selectedCategoryHotkeys().length === 0) {
                        <div data-testid="hotkeys-cheatsheet-empty" class="text-center py-12">
                            <i class="fas fa-search text-gray-300 dark:text-gray-600 text-4xl mb-4"></i>
                            <p class="text-gray-500 dark:text-gray-400 text-lg">{{ 'hotkeys.no-shortcuts-found' | translate }}</p>
                            <p class="text-gray-400 dark:text-gray-500 text-sm mt-2">{{ 'hotkeys.try-adjusting-search' | translate }}</p>
                        </div>
                    } @else {
                        <div>
                            <h3 class="text-xl font-semibold text-gray-800 dark:text-gray-100 mb-4">
                                {{ selectedCategory() === ALL_CATEGORIES ? ('hotkeys.all-categories' | translate) : selectedCategory() }}
                            </h3>
                            @if (selectedCategory() === ALL_CATEGORIES) {
                                <!-- Show grouped by category when "All Categories" is selected -->
                                @for (group of groupedHotkeys(); track group.category) {
                                    <div class="mb-6">
                                        <h4 class="text-lg font-semibold text-gray-700 dark:text-gray-300 mb-3 pb-2 border-b border-gray-200 dark:border-gray-700">
                                            {{ group.category }}
                                        </h4>
                                        <div class="space-y-2">
                                            @for (hotkey of group.hotkeys; track $index) {
                                                <div data-testid="hotkeys-shortcut-row" class="flex items-center justify-between gap-4 p-3 border border-gray-200 dark:border-gray-700 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-800/50 transition-colors">
                                                    <div class="flex-1 min-w-0">
                                                        <span class="text-sm text-gray-700 dark:text-gray-300">
                                                            {{ getHotkeyDescription(hotkey) | translate }}
                                                        </span>
                                                    </div>
                                                    <div class="flex items-center gap-4 flex-shrink-0">
                                                        @if (!showCustomizeMode()) {
                                                            <!-- Default Shortcuts (view mode only) -->
                                                            <div class="flex items-center gap-1.5">
                                                                @if (hotkey.formatted.length > 0) {
                                                                    @for (key of hotkey.formatted; track key; let i = $index) {
                                                                        <kbd class="px-2.5 py-1 text-xs font-semibold text-gray-800 dark:text-gray-200 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm whitespace-nowrap">
                                                                            {{ key }}
                                                                        </kbd>
                                                                        @if (i < hotkey.formatted.length - 1) {
                                                                            <span class="text-gray-400 dark:text-gray-500 text-xs flex-shrink-0">+</span>
                                                                        }
                                                                    }
                                                                } @else {
                                                                    <span class="text-gray-400 dark:text-gray-500 text-xs italic">{{ 'hotkeys.no-shortcut' | translate }}</span>
                                                                }
                                                            </div>
                                                        } @else {
                                                            <!-- Custom Input (customize mode only) -->
                                                            <app-shortcut-input
                                                                [combos]="getFilteredCustomShortcut(hotkey)"
                                                                [excludeButtonId]="getExcludeButtonId(hotkey)"
                                                                [excludeActionKey]="getExcludeActionKey(hotkey)"
                                                                [shortcutRegistry]="shortcutRegistry"
                                                                [compact]="true"
                                                                [shouldIncludeCombo]="platformComboFilter"
                                                                (combosChange)="onFilteredCombosChange(hotkey, $event)"
                                                            />
                                                        }
                                                    </div>
                                                </div>
                                            }
                                        </div>
                                    </div>
                                }
                            } @else {
                                <!-- Show single category -->
                                <div class="space-y-2">
                                    @for (hotkey of selectedCategoryHotkeys(); track $index) {
                                        <div class="flex items-center justify-between gap-4 p-3 border border-gray-200 dark:border-gray-700 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-800/50 transition-colors">
                                            <div class="flex-1 min-w-0">
                                                <span class="text-sm text-gray-700 dark:text-gray-300">
                                                    {{ getHotkeyDescription(hotkey) | translate }}
                                                </span>
                                            </div>
                                            <div class="flex items-center gap-4 flex-shrink-0">
                                                @if (!showCustomizeMode()) {
                                                    <!-- Default Shortcuts (view mode only) -->
                                                    <div class="flex items-center gap-1.5">
                                                        @if (hotkey.formatted.length > 0) {
                                                            @for (key of hotkey.formatted; track key; let i = $index) {
                                                                <kbd class="px-2.5 py-1 text-xs font-semibold text-gray-800 dark:text-gray-200 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm whitespace-nowrap">
                                                                    {{ key }}
                                                                </kbd>
                                                                @if (i < hotkey.formatted.length - 1) {
                                                                    <span class="text-gray-400 dark:text-gray-500 text-xs flex-shrink-0">+</span>
                                                                }
                                                            }
                                                        } @else {
                                                            <span class="text-gray-400 dark:text-gray-500 text-xs italic">{{ 'hotkeys.no-shortcut' | translate }}</span>
                                                        }
                                                    </div>
                                                } @else {
                                                    <!-- Custom Input (customize mode only) -->
                                                    <app-shortcut-input
                                                        [combos]="getFilteredCustomShortcut(hotkey)"
                                                        [excludeButtonId]="getExcludeButtonId(hotkey)"
                                                        [excludeActionKey]="getExcludeActionKey(hotkey)"
                                                        [shortcutRegistry]="shortcutRegistry"
                                                        [compact]="true"
                                                        [shouldIncludeCombo]="platformComboFilter"
                                                        (combosChange)="onFilteredCombosChange(hotkey, $event)"
                                                    />
                                                }
                                            </div>
                                        </div>
                                    }
                                </div>
                            }
                            @if (showCustomizeMode()) {
                                <div class="flex items-center justify-end gap-3 pt-4 mt-4 border-t border-gray-200 dark:border-gray-700">
                                    <button
                                        (click)="resetCustomShortcuts()"
                                        class="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 rounded-lg transition-colors"
                                    >
                                        {{ 'hotkeys.reset-all' | translate }}
                                    </button>
                                    <button
                                        (click)="saveCustomShortcuts()"
                                        class="px-4 py-2 text-sm font-medium text-white bg-blue-600 dark:bg-blue-500 hover:bg-blue-700 dark:hover:bg-blue-600 rounded-lg transition-colors"
                                    >
                                        <i class="fas fa-save mr-2"></i>
                                        {{ 'common.save' | translate }}
                                    </button>
                                </div>
                            }
                        </div>
                    }
                </div>
            </div>

            <!-- Footer -->
            <div class="p-4 border-t border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-900/50 flex items-center justify-between text-sm text-gray-500 dark:text-gray-400">
                <div>
                    <span data-testid="hotkeys-cheatsheet-count">{{ 'hotkeys.shortcut-count' | translate: { count: hotkeys().length } }}</span>
                    @if (searchQuery()) {
                        <span class="ml-2">({{ 'hotkeys.filtered-count' | translate: { count: filteredHotkeysCount() } }})</span>
                    }
                </div>
            </div>
        </div>
    </div>
</div>
}
